import type { ReactNode } from 'react';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { GenericRadioTextFilter } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextFilter';
import type { AbstractSingleFilterPillsProps } from '../SingleFilterType';
import type { RadioTextFilterValueType } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextType';
import { RadioTextFilterOperation } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextType';
import { getLabels } from '../../../../utils/LabelFormatter/SingleLabelFormatter/URLLabelFormatter';
import type { FilterKindEnum } from '../../../../domains/global-filters/model/FilterChain';
import { getFilterMetadata } from '../../FilterChainUtils';

interface URLFilterParmas extends AbstractSingleFilterPillsProps<RadioTextFilterValueType> {
  filterKind: FilterKindEnum;
  header?: ReactNode;
  optionalPlaceholders?: Partial<Record<RadioTextFilterOperation, string>>;
  isFunnelStep?: boolean;
}
const options = [
  RadioTextFilterOperation.CONTAINS,
  RadioTextFilterOperation.NOT_CONTAINS,
  RadioTextFilterOperation.STARTS_WITH,
  RadioTextFilterOperation.NOT_STARTS_WITH,
  RadioTextFilterOperation.ENDS_WITH,
  RadioTextFilterOperation.NOT_ENDS_WITH,
  RadioTextFilterOperation.IS,
  RadioTextFilterOperation.IS_NOT,
  RadioTextFilterOperation.IS_EXACTLY,
  RadioTextFilterOperation.IS_NOT_EXACTLY,
  RadioTextFilterOperation.REGEX,
];
const funnelStepOptions = [
  RadioTextFilterOperation.CONTAINS,
  RadioTextFilterOperation.STARTS_WITH,
  RadioTextFilterOperation.ENDS_WITH,
  RadioTextFilterOperation.IS,
  RadioTextFilterOperation.IS_EXACTLY,
  RadioTextFilterOperation.REGEX,
];
const placeholders = {
  [RadioTextFilterOperation.CONTAINS]: 'e.g. register',
  [RadioTextFilterOperation.NOT_CONTAINS]: 'e.g. register',
  [RadioTextFilterOperation.STARTS_WITH]: 'e.g. https://example.com/',
  [RadioTextFilterOperation.NOT_STARTS_WITH]: 'e.g. https://example.com/',
  [RadioTextFilterOperation.ENDS_WITH]: 'e.g. /contact',
  [RadioTextFilterOperation.NOT_ENDS_WITH]: 'e.g. /contact',
  [RadioTextFilterOperation.IS_EXACTLY]: 'e.g. https://example.com/page',
  [RadioTextFilterOperation.IS_NOT_EXACTLY]: 'e.g. https://example.com/page',
  [RadioTextFilterOperation.IS]: 'e.g. example.com/page',
  [RadioTextFilterOperation.IS_NOT]: 'e.g. example.com/page',
  [RadioTextFilterOperation.REGEX]: 'e.g. /page/[^/]+',
};

export function BaseURLFilter({
  currentValue,
  autoFocus,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  filterKind,
  header,
  optionalPlaceholders,
  isFunnelStep,
}: URLFilterParmas): JSX.Element {
  const helpHotjarUrl = useHelpHotjarUrl();
  const metadata = getFilterMetadata(filterKind);
  const { label1, label2 } = getLabels(currentValue, filterKind);
  return (
    <GenericRadioTextFilter
      autoFocus={autoFocus}
      filterName={metadata.filterName}
      iconName={metadata.iconName ?? 'navigate'}
      currentValue={currentValue}
      onApply={onApply}
      onRemove={onRemove}
      placeholders={{
        ...placeholders,
        ...optionalPlaceholders,
      }}
      options={isFunnelStep === true ? funnelStepOptions : options}
      onDropdownHidden={onDropdownHidden}
      showDropdownOnMount={showDropdownOnMount}
      label1={label1}
      label2={label2}
      header={header}
      propertyName="URL"
      dropdownStyle="withSelect"
      infoFooter={{
        link: `${helpHotjarUrl}/articles/8983796165911`,
        label: 'More about filtering options',
      }}
      isFunnelStep={isFunnelStep}
    />
  );
}
